import { Routes, Route, BrowserRouter } from "react-router-dom";
import { logEvent } from "./compornents/Analytics";
import { Client as Styletron } from "styletron-engine-monolithic";
import { Provider as StyletronProvider } from "styletron-react";
import { LightTheme, BaseProvider } from "baseui";
import { Top } from "./screens/Top";
import { RegisterStep1 } from "./screens/register/RegisterStep1";
import { RegisterStep2 } from "./screens/register/RegisterStep2";
import { RegisterStep3 } from "./screens/register/RegisterStep3";
import { RegisterStep4 } from "./screens/register/RegisterStep4";
import { RegisterMenu } from "./screens/register/RegisterMenu";
import { RegisterPostcode } from "./screens/register/RegisterPostcode";
import { Register } from "./screens/register/Register";
import { RegisterAuth } from "./screens/register/RegisterAuth";
import { RegisterProfile } from "./screens/register/RegisterProfile";
import { OrderStep0 } from "./screens/order_step/OrderStep0";
import { OrderStep1 } from "./screens/order_step/OrderStep1";
import { OrderStep2 } from "./screens/order_step/OrderStep2";
import { OrderStep3 } from "./screens/order_step/OrderStep3";
import { OrderStep4 } from "./screens/order_step/OrderStep4";
import { OrderStep5 } from "./screens/order_step/OrderStep5";
import { Prepare } from "./screens/Prepare";
import Home from "./screens/Home";
import OrderDetail from "./screens/Home/[id]";
import { OptionCategory } from "./screens/OptionCategory";
import { Options } from "./screens/Options";
import { Option } from "./screens/Option";
import { Confirm } from "./screens/Confirm";
import { OrderDone } from "./screens/OrderDone";
import { Address } from "./screens/Address";
import { Location } from "./screens/Location";
import { Location2 } from "./screens/Location2";
import { Notfound } from "./screens/Notfound";
import { Orders } from "./screens/Orders";
import { Order } from "./screens/Order";
import { Account } from "./screens/Account";
import { Profile } from "./screens/Profile";
import { Cleaning } from "./screens/Cleaning";
import { Other } from "./screens/Other";
import { Privacy } from "./screens/Privacy";
import { Rule } from "./screens/Rule";
import { Reagree } from "./screens/Reagree";
import { Tokutei } from "./screens/Tokutei";
import { Company } from "./screens/Company";
import { Baisyo } from "./screens/Baisyo";
import { Leave } from "./screens/Leave";
import { Credit } from "./screens/Credit";
import { RealTimeMap } from "./screens/RealTimeMap";
import { Coupon } from "./screens/Coupon";
import { Survey } from "./screens/Survey";
import { Inquiry } from "./screens/Inquiry";
import { StaffVisit } from "./screens/StaffVisit";
import { Closet } from "./screens/Closet";
import { ClosetAdd } from "./screens/ClosetAdd";
import { ClosetDetail } from "./screens/ClosetDetail";
import { Syokai } from "./screens/Syokai";
import "./css/App.css";
import './css/Bootstrap.css';
import './css/tokens.css';   // デザイントークン（変数定義）
import './css/refresh.css';  // UIリフレッシュ層（既存の後に読み込んで上書き）
import { ToastContainer } from 'react-toastify';
import { Login } from "./screens/Login";
import { LoginAuth } from "./screens/LoginAuth";
import { LineCallback } from "./screens/LineCallback";
import { LinePhone } from "./screens/LinePhone";
import { LinePhoneAuth } from "./screens/LinePhoneAuth";
import { ErrorBoundary } from "./compornents/ErrorBoundary";
import { Help } from "./screens/help/Help";
import { SelfGuide } from "./screens/self_guide/SelfGuide";
import { SelfGuideFaq } from "./screens/self_guide/SelfGuideFaq";
import { SelfSpots } from "./screens/self_guide/SelfSpots";
import { Help1 } from "./screens/help/Help1";
import { Help2 } from "./screens/help/Help2";
import { Notifications } from "./screens/Notifications";
import { Notification } from "./screens/Notification";
import { Notice } from "./screens/Notice";
import { CustomerService } from "./screens/CustomerService";
import { MenuDetail } from "./screens/menu_detail/MenuDetail";
import { useEffect } from "react";
import { APIProvider } from '@vis.gl/react-google-maps';

const engine = new Styletron();
const APP_VERSION = "1.10.33";

function App() {
  useEffect(() => {
    // 紹介リンク(now.laundrypack.app/?syokai_code=...)から来た場合、紹介コードを保持。
    // 登録時(RegisterProfile)が localStorage の syokai_code を参照するため。
    try {
      const sp = new URLSearchParams(window.location.search);
      const sc = sp.get('syokai_code');
      if (sc) localStorage.setItem('syokai_code', sc);
      // 流入元アトリビューション: LPのCTA(now.laundrypack.app/?ref=lp / lp_uber)から来た場合、
      // アプリのセッションIDで lp_in を1回記録（LP→アプリ→注文をD1で同一セッション追跡できる）。
      const ref = sp.get('ref') || sp.get('utm_source');
      if (ref) {
        sessionStorage.setItem('lp_ref', ref);
        // 登録完了(RegisterProfile)まで流入元を保持したいので localStorage にも残す。
        // ロッカーQR(?ref=locker_{lpspot_id})経由の登録数を追うため。syokai_code と同じ流儀。
        localStorage.setItem('lp_ref', ref);
        if (!sessionStorage.getItem('lp_in_sent')) {
          sessionStorage.setItem('lp_in_sent', '1');
          logEvent('lp_in', { ref });
        }
      }
    } catch (e) { /* no-op */ }
    // バージョンチェックとキャッシュクリア（2026-09-01 方式変更）。
    // 旧方式は「読み込まれたJSに埋め込まれた版数 vs localStorage」の比較だったため、
    // PWAが古いindex.htmlごとキャッシュしていると古いJSしか動かず新版に気づけなかった。
    // サーバー上の manifest.json の version を真実として、起動時とアプリ復帰時
    // (visibilitychange) にキャッシュ無効で照合し、違えばキャッシュ削除→リロードする。
    const checkForUpdate = async () => {
      try {
        const res = await fetch(`/manifest.json?ts=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const mf = await res.json();
        if (!mf.version || mf.version === APP_VERSION) return;
        // 同じ版への再リロードを繰り返さない（配信直後のCDN不整合でのループ防止）
        if (sessionStorage.getItem('reloaded_for_version') === mf.version) return;
        sessionStorage.setItem('reloaded_for_version', mf.version);
        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map((n) => caches.delete(n)));
        }
        window.location.reload();
      } catch (e) { /* オフライン等は無視（次の復帰時に再チェック） */ }
    };
    checkForUpdate();
    const onVisible = () => { if (document.visibilityState === 'visible') checkForUpdate(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  return (
    <div className="LaundryPackApp">
      < ToastContainer
        position="top-center"
        autoClose={6000}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        theme="colored"
      />
      <StyletronProvider value={engine}>
        <BaseProvider theme={LightTheme}>
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
            <div>
              <BrowserRouter>
                <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<Top />} />
                  <Route path="/register/step1" element={<RegisterStep1 />} />
                  <Route path="/register/step2" element={<RegisterStep2 />} />
                  <Route path="/register/step3" element={<RegisterStep3 />} />
                  <Route path="/register/step4" element={<RegisterStep4 />} />
                  <Route path="/order_done/:order_id" element={<OrderDone />} />
                  <Route path="/register/menu" element={<RegisterMenu />} />
                  <Route path="/register/postcode" element={<RegisterPostcode />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/register/auth" element={<RegisterAuth />} />
                  <Route path="/register/profile" element={<RegisterProfile />} />
                  <Route path="/order_step/step0" element={<OrderStep0 />} />
                  <Route path="/order_step/step1" element={<OrderStep1 />} />
                  <Route path="/order_step/step2" element={<OrderStep2 />} />
                  <Route path="/order_step/step3" element={<OrderStep3 />} />
                  <Route path="/order_step/step4" element={<OrderStep4 />} />
                  <Route path="/order_step/step5" element={<OrderStep5 />} />
                  <Route path="/prepare" element={<Prepare />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/home/:id" element={<OrderDetail />} />
                  <Route path="/option" element={<Option />} />
                  <Route path="/confirm" element={<Confirm />} />
                  <Route path="/address" element={<Address />} />
                  <Route path="/location" element={<Location />} />
                  <Route path="/location2" element={<Location2 />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/order/:order_id" element={<Order />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/cleaning" element={<Cleaning />} />
                  <Route path="/other" element={<Other />} />
                  <Route path="/company" element={<Company />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/rule" element={<Rule />} />
                  <Route path="/reagree" element={<Reagree />} />
                  <Route path="/tokutei" element={<Tokutei />} />
                  <Route path="/baisyo" element={<Baisyo />} />
                  <Route path="/leave" element={<Leave />} />
                  <Route path="/credit" element={<Credit />} />
                  <Route path="/real_time_map" element={<RealTimeMap />} />
                  <Route path="/coupon" element={<Coupon />} />
                  <Route path="/survey" element={<Survey />} />
                  <Route path="/inquiry" element={<Inquiry />} />
                  <Route path="/staff_visit" element={<StaffVisit />} />
                  {/* お気に入りクローゼット（D案・RFDB連携）。APIが enabled:0 の環境では「準備中」を出す */}
                  <Route path="/closet" element={<Closet />} />
                  <Route path="/closet/add" element={<ClosetAdd />} />
                  <Route path="/closet/:id" element={<ClosetDetail />} />
                  <Route path="/syokai" element={<Syokai />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/login/auth" element={<LoginAuth />} />
                  <Route path="/line/callback" element={<LineCallback />} />
                  <Route path="/line/phone" element={<LinePhone />} />
                  <Route path="/line/phone/auth" element={<LinePhoneAuth />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="/help1" element={<Help1 />} />
                  <Route path="/help2" element={<Help2 />} />
                  <Route path="/self_guide" element={<SelfGuide />} />
                  <Route path="/self_guide/faq" element={<SelfGuideFaq />} />
                  <Route path="/self_spots" element={<SelfSpots />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/notice/:token" element={<Notice />} />
                  <Route path="/notification/:notification_id" element={<Notification />} />
                  <Route path="/option_category" element={<OptionCategory />} />
                  <Route path="/menu_detail" element={<MenuDetail />} />
                  <Route path="/options" element={<Options />} />
                  <Route path="/option" element={<Option />} />
                  <Route path="/customer_service" element={<CustomerService />} />
                  <Route path="*" element={<Notfound />} />
                </Routes>
                </ErrorBoundary>
              </BrowserRouter>
            </div>
          </APIProvider>
        </BaseProvider>
      </StyletronProvider>
    </div>

  );
}

export default App;
